import { EngineIcon } from '@phosphor-icons/react';

import { MapOverlay } from '@/components/maps';
import { truncateLabel } from '@/lib/maps/labels';
import type { PixelPoint } from '@/lib/maps/types';

import type { MapDevice } from '../map-schemas';

import styles from './device-markers.module.scss';

export type DeviceMarkersProps = {
  /**
   * The Devices actually drawn as individual markers — already filtered by the caller through
   * `plottableDevices()`. Keeping the filter outside means this layer and ticket 07's click
   * arbitration cannot disagree about what is on the map. Ticket 08 will subtract clustered Devices
   * here too, for the same reason.
   */
  devices: readonly MapDevice[];
  /**
   * Where a Device sits relative to its own coordinates, for the Devices that share a point with
   * another (`fanOutOffsets`). Absent for everything else, which is what keeps 113587 §1c — no
   * snapping, no offset, drawn exactly where it reported — true of every ordinary marker.
   *
   * The caller decides which ring: the resting one 113586 §7a asks for, or the wider one for the
   * group it has clicked (§7b). This layer only places what it is handed.
   */
  fanOffsets?: ReadonlyMap<string, PixelPoint>;
  /** The focused Field, if any. Devices outside it are de-emphasised (113586 §3c). Ticket 07 supplies
   *  the real value; until then the page passes `null`. */
  focusedFieldId?: string | null;
  /**
   * Which Devices show their name label (113586 §3b). Not derived here: the set is
   * `labelledDeviceIds()` narrowed by `visibleLabels()`, and collision suppression needs projected
   * positions the caller already has. Keeping the decision outside means this layer and the collision
   * rule cannot disagree about which chips are on screen.
   */
  labelled?: ReadonlySet<string>;
  /** Wired by ticket 11 to open the Device quick view. Optional here so that ticket adds a handler
   *  without reshaping this contract — but the `<button>` itself is not optional: 113587 §3b requires
   *  every marker to be clickable, unassociated ones included. */
  onSelect?: (deviceId: string) => void;
  /**
   * Whether a marker is a control. `false` draws the Device as **context** — the same pin, the same
   * accessible name, but no button, no tab stop and no click — which is what Field Mapping mode's
   * "displayed, and not clickable" means (113600, Field Mapping Screen Elements). Defaults to
   * `true`, so the Map keeps 113587 §3b's "every marker is clickable, unassociated ones included".
   */
  interactive?: boolean;
};

/**
 * The Grower's located Devices as markers on the canvas (Figma `948:567818` — a 40 px `Error/700`
 * circle carrying a 20 px white Engine glyph).
 *
 * Each sits at **exactly** the coordinates the Device reported: `device.location` is the only position
 * anywhere in this path, with no snapping, no centroid and no nearest-road correction (113587 §1c).
 * Several Devices at the same point all render, stacked, and all stay in the DOM (§4a) —
 * `fanOffsets` spreads them onto a ring so none is hidden behind another (113586 §7a) and each is
 * individually reachable once the group is clicked (§7b). That can only ever happen at maximum
 * zoom, where ticket 08's `maxZoom: MAX_ZOOM - 1` leaves them un-clustered.
 *
 * An **unplaced device** never reaches here at all: ticket 04's endpoint omits it from `data` and
 * counts it in `meta.unplacedCount`, and `plottableDevices()` drops anything left that cannot be
 * plotted. That is the whole of §5a–§5b — no placeholder, no default position, no approximate one.
 *
 * **Styling is identical for every Device Status**, because `status` is never read here — which makes
 * §6a/§6b structural rather than something to remember. The one thing that changes a marker's
 * appearance is Field focus, and it changes depth only (113586 §3c), so de-emphasis can never be
 * mistaken for a status.
 *
 * The form is deliberately unlike **My location** (ticket 13), which is a bare dot with no glyph and no
 * accuracy halo: §1a/§1c requires the two never be confused, and shape is what delivers it.
 */
export function DeviceMarkers({
  devices,
  fanOffsets,
  focusedFieldId = null,
  labelled,
  onSelect,
  interactive = true,
}: DeviceMarkersProps) {
  return (
    <>
      {devices.map((device) => {
        const muted = focusedFieldId !== null && device.fieldId !== focusedFieldId;
        const offset = fanOffsets?.get(device.id);
        const markerClassName = [styles.marker, muted ? styles.muted : '']
          .filter(Boolean)
          .join(' ');
        const pin = (
          <span className={styles.pin} aria-hidden>
            <EngineIcon size={20} />
          </span>
        );

        return (
          <MapOverlay key={device.id} point={device.location}>
            <span
              className={styles.stack}
              style={offset ? { left: offset.x, top: offset.y } : undefined}
            >
              {/* Figma 823:701656. `aria-hidden`: the marker `<button>` already carries the Device's
                  FULL name as its accessible name, and the chip is the same name truncated for the
                  canvas — announcing both would read it twice, the second time cut off. */}
              {labelled?.has(device.id) ? (
                <span className={styles.label} aria-hidden>
                  {truncateLabel(device.label)}
                </span>
              ) : null}
              {interactive ? (
                <button
                  type="button"
                  className={markerClassName}
                  aria-label={device.label}
                  onClick={() => onSelect?.(device.id)}
                >
                  {pin}
                </button>
              ) : (
                <span className={markerClassName} aria-label={device.label}>
                  {pin}
                </span>
              )}
            </span>
          </MapOverlay>
        );
      })}
    </>
  );
}
